import React, { useState } from "react";

import { ObserveState } from "../../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../../rule-engine-core/Common/StateManager";
import { ConfirmDialog } from "../../../../CommonUI/ConfirmDialog";

import { RuleTableEditContext } from "../../../../rule-engine-core/Rule/Table/Rule/RuleTableEditContext";
import { RuleTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Rule/RuleTableConfiguration";

import { RuleTableHeader } from "./RuleTableHeader";
import { PlusGrayIcon } from "../../../../CommonUI/Icon/PlusGrayIcon";
import { Notification } from "../../../../CommonUI/BuilderUI/Notification";
import {
    RuleDesignerRender,
    RuleEditorInTester,
    RuleEditorRender,
    RuleLabelColumn,
    RuleTableEmptyAddColumn,
    RuleTableEmptyColumn,
    RuleTableEmptyIndexColumn,
    RuleToolbarRender,
} from "../../../DesignUI/CommonStyles";
import { SettingButton } from "../../../DesignUI/SettingButton";

import { XResultCell } from "../../CommonUI/XResultCell";
import { ObserveMultiState } from "../../../../CommonUI/StateManagement/ObserveMultiState";
import { ValidationInformation } from "../../../DesignUI/ValidationInformation";
import { DockedSidebar } from "../../../../CommonUI/BuilderUI/DockSidebar";
import { XRuleTableRow } from "./RuleTableRow";
import { TestContext } from "../../../../CommonUI/Testing/TestContext";
import {
    ITableRowTestValue,
    TableRowTestContext,
} from "../../../../rule-engine-core/Rule/Table/Common/TableRowTestContext";
import { TestResult } from "../../../../CommonUI/Testing/TestResult";
import { DataUtilities } from "../../../../rule-engine-core/Common/DataUtilities";
import { UILabel } from "../../../../CommonUI/BuildlingBlocks/UILabel";
import { UISpacedBoxContent } from "../../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { PropertyEditorBoolean } from "../../../../CommonUI/PropertyEditing/PropertyEditorBoolean";

interface IRuleTableDesigner {
    editContext: RuleTableEditContext;
    ruleDefinition: RuleTableConfiguration;
}

export const RuleTableDesigner = (props: IRuleTableDesigner) => {
    const { ruleDefinition, editContext } = props;

    const [openSettingsLayer, setOpenSettingsLayer] = useState<boolean>(false);

    const testContext = React.useContext(TestContext);

    const onAddNewRow = () => {
        if (testContext) return;

        ruleDefinition.addRow();
        StateManager.propertyChanged(ruleDefinition, "rows");
    };

    const isDefaultTestResult =
        !!testContext &&
        !testContext.isError &&
        !(testContext?.items || []).find((rowResult) => rowResult.result === "true");

    if (testContext) {
        return (
            <RuleEditorInTester>
                <RuleEditorRender
                    columnCount={ruleDefinition.ruleColumns.length + ruleDefinition.resultColumns.length + 1}
                    className="RuleEditorRender"
                    rowsGap={6}
                >
                    <RuleTableHeader {...props} />

                    <>
                        {ruleDefinition.rows.map((row, idx) => {
                            return (
                                <XRuleTableRow
                                    editContext={editContext}
                                    row={row}
                                    ruleDefinition={ruleDefinition}
                                    rowIndex={idx}
                                    key={row._id}
                                />
                            );
                        })}
                    </>
                    <RuleTableEmptyIndexColumn data-testid="btn-rule-table-add-new-row"></RuleTableEmptyIndexColumn>
                    {ruleDefinition.ruleColumns.map((_, idx) => {
                        return <RuleTableEmptyColumn key={idx} />;
                    })}
                    <RuleTableEmptyAddColumn />
                    {ruleDefinition.resultColumns.map((col) => {
                        return <RuleTableEmptyColumn key={col._id} />;
                    })}
                    <RuleTableEmptyAddColumn />
                    <RuleTableEmptyAddColumn />
                    {ruleDefinition.allowDefaultResults && (
                        <TableRowTestContext.Provider
                            value={isDefaultTestResult ? ITableRowTestValue.PASSED : undefined}
                        >
                            <RuleLabelColumn
                                colSpan={ruleDefinition.ruleColumns.length + 1}
                                colStart={1}
                                y={ruleDefinition.rows.length + 3}
                            >
                                Default Result Values
                            </RuleLabelColumn>

                            {ruleDefinition.resultColumns.map((rescol, index2) => {
                                return (
                                    <ObserveState
                                        key={rescol._id}
                                        listenTo={rescol}
                                        control={() => (
                                            <XResultCell
                                                ruleDefinition={ruleDefinition}
                                                resultColumn={rescol}
                                                editContext={editContext}
                                                resultCell={ruleDefinition.defaultResult[rescol._id]}
                                                rowIndex={ruleDefinition.rows.length + 3}
                                                cellIndex={ruleDefinition.ruleColumns.length + 2 + index2}
                                            />
                                        )}
                                    />
                                );
                            })}
                        </TableRowTestContext.Provider>
                    )}
                </RuleEditorRender>
                <TestResult executableId={ruleDefinition._id} />
            </RuleEditorInTester>
        );
    }

    return (
        <RuleDesignerRender>
            <RuleToolbarRender>
                <SettingButton onClick={() => setOpenSettingsLayer(true)} />
            </RuleToolbarRender>
            <DockedSidebar
                sidebar={() => {
                    return (
                        <UISpacedBoxContent>
                            <UILabel>Settings</UILabel>
                            <ObserveState
                                listenTo={ruleDefinition}
                                control={() => (
                                    <PropertyEditorBoolean
                                        editObject={ruleDefinition}
                                        propertyName={"allowDefaultResults"}
                                        label={"Allow Default Results"}
                                    />
                                )}
                            />
                        </UISpacedBoxContent>
                    );
                }}
                content={() => (
                    <ObserveState
                        listenTo={ruleDefinition}
                        properties={["ruleColumns", "resultColumns"]}
                        control={() => {
                            return (
                                <RuleEditorRender
                                    columnCount={
                                        ruleDefinition.ruleColumns.length + ruleDefinition.resultColumns.length + 1
                                    }
                                >
                                    <RuleTableHeader {...props} />
                                    <ObserveState
                                        listenTo={ruleDefinition}
                                        properties={["rows"]}
                                        control={() => {
                                            return (
                                                <>
                                                    {ruleDefinition.rows.map((row, idx) => {
                                                        return (
                                                            <XRuleTableRow
                                                                editContext={editContext}
                                                                row={row}
                                                                ruleDefinition={ruleDefinition}
                                                                rowIndex={idx}
                                                                key={row._id}
                                                            />
                                                        );
                                                    })}
                                                </>
                                            );
                                        }}
                                    />
                                    <RuleTableEmptyIndexColumn
                                        onClick={onAddNewRow}
                                        data-testid="btn-rule-table-add-new-row"
                                    >
                                        {!testContext && <PlusGrayIcon />}
                                    </RuleTableEmptyIndexColumn>
                                    {ruleDefinition.ruleColumns.map((_, idx) => {
                                        return <RuleTableEmptyColumn key={idx} />;
                                    })}
                                    <RuleTableEmptyAddColumn />
                                    {ruleDefinition.resultColumns.map((col) => {
                                        return <RuleTableEmptyColumn key={col._id} />;
                                    })}
                                    <RuleTableEmptyAddColumn />
                                    <RuleTableEmptyAddColumn />

                                    <ObserveState
                                        listenTo={ruleDefinition}
                                        properties={["allowDefaultResults"]}
                                        control={() => {
                                            if (!ruleDefinition.allowDefaultResults) return null;

                                            return (
                                                <>
                                                    <RuleLabelColumn
                                                        colSpan={ruleDefinition.ruleColumns.length + 1}
                                                        colStart={1}
                                                    >
                                                        Default Result Values
                                                    </RuleLabelColumn>

                                                    {ruleDefinition.resultColumns.map((rescol) => {
                                                        return (
                                                            <ObserveState
                                                                key={rescol._id}
                                                                listenTo={rescol}
                                                                control={() => (
                                                                    <XResultCell
                                                                        ruleDefinition={ruleDefinition}
                                                                        resultColumn={rescol}
                                                                        editContext={editContext}
                                                                        resultCell={
                                                                            ruleDefinition.defaultResult[rescol._id]
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                        );
                                                    })}
                                                </>
                                            );
                                        }}
                                    />
                                </RuleEditorRender>
                            );
                        }}
                    />
                )}
                sidebarWidth={350}
                open={openSettingsLayer}
                onClose={() => setOpenSettingsLayer(false)}
            />

            <Notification editContext={editContext} />
            <ObserveMultiState
                listenTo={[
                    ...props.ruleDefinition.ruleColumns,
                    ...props.ruleDefinition.resultColumns,
                    props.ruleDefinition,
                ]}
                control={() => <ValidationInformation provider={ruleDefinition} />}
            />
            <ObserveState
                listenTo={props.editContext}
                properties={["deletingColumn"]}
                control={() => {
                    if (props.editContext.deletingColumn) {
                        return (
                            <ConfirmDialog
                                open={true}
                                onConfirm={(dontAskAgain) => {
                                    if (dontAskAgain) {
                                        DataUtilities.setCookieValue("RULE_TABLE_DELETE_CONFIRM", "true");
                                    }

                                    props.editContext.forceRemoveColumn(props.editContext.deletingColumn!);
                                    StateManager.propertyChanged(props.ruleDefinition, "ruleColumns");
                                    props.editContext.deletingColumn = undefined;
                                }}
                                onCancel={() => (props.editContext.deletingColumn = undefined)}
                            />
                        );
                    }
                    return null;
                }}
            />
        </RuleDesignerRender>
    );
};
